import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../App";
import {
  AlertTriangle,
  TrendingUp,
  CheckCircle2,
  Lock,
  Clock,
  Package,
  Mail,
  ExternalLink
} from "lucide-react";

// ============ ADMIN ACCESS CONTROL ============
const DEMO_ADMIN_EMAILS = [
  "reynir@replydriven.com",
  "reyniroskarsson2310@gmail.com"
];
const DEMO_SECRET_KEY = "rd_demo_2026";

// ============ DEMO EMAIL DATA ============
const DEMO_EMAIL_DATA = {
  subject: "Your weekly profit insight is ready",
  preview_text: "High cart abandonment detected on mobile checkout...",
  store_name: "Outdoor Essentials Co.",
  date_range: "Jan 13 – Jan 19, 2026",
  insight: {
    title: "High cart abandonment on mobile checkout",
    explanation: "Mobile checkout completion dropped 18% this week. Customers are adding items to cart but leaving at the payment step more frequently than desktop users.",
    impact: "Mobile traffic accounts for 68% of your sessions but only 41% of completed orders. This gap suggests friction in your mobile checkout flow.",
    action: "Review your mobile checkout for unnecessary form fields or slow-loading payment options. Consider enabling Shop Pay or Apple Pay."
  }
};

const DemoEmailPreview = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    const secretKey = searchParams.get("key");
    if (
      (user?.email && DEMO_ADMIN_EMAILS.includes(user.email.toLowerCase())) ||
      secretKey === DEMO_SECRET_KEY
    ) {
      setAuthorized(true);
    } else if (user !== undefined) {
      const timer = setTimeout(() => {
        if (!authorized) navigate("/");
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [user, searchParams, navigate, authorized]);

  if (!authorized) {
    return (
      <div className="min-h-screen bg-[#f5f5f5] flex items-center justify-center">
        <div className="text-center">
          <Lock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">Checking access...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f5f5]">
      {/* Email Client Chrome - Minimal */}
      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-gray-400" />
            <span className="text-sm text-gray-600">Inbox</span>
          </div>
          <span className="text-xs text-gray-400">Monday, Jan 20, 2026 • 9:00 AM</span>
        </div>
      </div>

      {/* Email Container */}
      <div className="max-w-3xl mx-auto py-8 px-4">
        {/* Email Header */}
        <div className="bg-white rounded-t-lg border border-gray-200 border-b-0 px-6 py-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#D4F34A] flex items-center justify-center">
                <span className="font-bold text-black text-sm">RD</span>
              </div>
              <div>
                <p className="font-semibold text-gray-900">ReplyDriven</p>
                <p className="text-sm text-gray-500">hello@replydriven.com</p>
              </div>
            </div>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-1">{DEMO_EMAIL_DATA.subject}</h1>
          <p className="text-sm text-gray-500">To: merchant@{DEMO_EMAIL_DATA.store_name.toLowerCase().replace(/\s+/g, '')}.com</p>
        </div>

        {/* Email Body */}
        <div className="bg-[#0B0F0E] rounded-b-lg border border-gray-200 border-t-0 overflow-hidden">
          {/* Email Content */}
          <div className="p-8">
            {/* Logo */}
            <div className="flex items-center gap-2 mb-8">
              <div className="w-8 h-8 rounded bg-[#D4F34A] flex items-center justify-center">
                <span className="font-bold text-black text-xs">RD</span>
              </div>
              <span className="font-semibold text-white">ReplyDriven</span>
            </div>

            {/* Greeting */}
            <p className="text-gray-300 mb-6">
              Here's your weekly profit insight for <span className="text-white font-medium">{DEMO_EMAIL_DATA.store_name}</span>
            </p>

            {/* Date Range */}
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-6">
              <Clock className="w-4 h-4" />
              <span>{DEMO_EMAIL_DATA.date_range}</span>
            </div>

            {/* Insight Card */}
            <div className="bg-gradient-to-br from-[#FF453A]/10 to-transparent border border-[#FF453A]/20 rounded-xl p-6 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#FF453A]/20 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-[#FF453A]" />
                </div>
                <span className="text-[#FF453A] text-sm font-medium uppercase tracking-wider">This Week's Insight</span>
              </div>
              
              <h2 className="text-xl font-semibold text-white mb-4">{DEMO_EMAIL_DATA.insight.title}</h2>
              
              <div className="space-y-4 text-gray-300">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">What Changed</p>
                  <p>{DEMO_EMAIL_DATA.insight.explanation}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Why This Matters</p>
                  <p>{DEMO_EMAIL_DATA.insight.impact}</p>
                </div>
              </div>
            </div>

            {/* Action Card */}
            <div className="bg-gradient-to-br from-[#D4F34A]/10 to-transparent border border-[#D4F34A]/20 rounded-xl p-6 mb-8">
              <div className="flex items-center gap-2 text-[#D4F34A] mb-3">
                <CheckCircle2 className="w-5 h-5" />
                <span className="text-sm font-medium uppercase tracking-wider">What to Fix Next</span>
              </div>
              <p className="text-white">{DEMO_EMAIL_DATA.insight.action}</p>
            </div>

            {/* CTA Button */}
            <div className="text-center mb-8">
              <a 
                href="#" 
                className="inline-flex items-center gap-2 bg-[#D4F34A] text-black font-semibold px-6 py-3 rounded-lg hover:bg-[#D4F34A]/90 transition-colors"
              >
                View Full Dashboard
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-700 pt-6 text-center">
              <p className="text-gray-500 text-sm mb-2">
                You're receiving this because you connected {DEMO_EMAIL_DATA.store_name} to ReplyDriven.
              </p>
              <p className="text-gray-600 text-xs">
                <a href="#" className="hover:text-gray-400">Unsubscribe</a> • <a href="#" className="hover:text-gray-400">Privacy Policy</a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Demo Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 py-2 px-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between text-xs text-gray-400">
          <span>Internal Demo • Not for public distribution</span>
          <span>Email Preview Template</span>
        </div>
      </div>
    </div>
  );
};

export default DemoEmailPreview;
